import React from "react";
import type { TranscriptItem } from "../hooks/useTranslator";
import { useUiLang } from "../../../context/UiLangContext";
import { Play, Square } from "lucide-react";

interface MinutaViewProps {
  meetingTranscript: TranscriptItem[];
  recording: boolean;
  onOpenSummary: () => void;
  onToggleRecording: () => void;
}

export const MinutaView: React.FC<MinutaViewProps> = ({
  meetingTranscript,
  recording,
  onOpenSummary,
  onToggleRecording,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  return (
    <div className="flex-1 flex flex-col p-6 overflow-hidden h-full">
      {/* Centered Start/Stop controls bar at the top */}
      <div className="flex justify-center mb-4">
        <button
          onClick={onToggleRecording}
          className={`py-3.5 px-8 text-sm font-semibold text-white rounded-xl flex items-center justify-center gap-2.5 cursor-pointer font-sans outline-none focus:ring-4 ${
            recording
              ? "bg-[var(--danger)] hover:bg-[#e03b30] focus:ring-[var(--danger)]/35 btn-stop-glow"
              : "bg-[var(--accent)] hover:bg-[#006ee6] focus:ring-[var(--accent)]/35 btn-start-glow"
          }`}
          style={{
            letterSpacing: "0.02em",
            backgroundColor: recording ? "var(--danger)" : "var(--accent)",
          }}
        >
          {recording ? (
            <Square size={13} fill="currentColor" />
          ) : (
            <Play size={15} fill="currentColor" />
          )}
          <span>{recording ? (isEn ? "Stop" : "Stop") : (isEn ? "Start" : "Start")}</span>
        </button>
      </div>

      <div className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 md:p-8 flex flex-col min-h-0 shadow-lg transition-colors duration-300">

        <div className="flex-1 overflow-y-auto space-y-3.5 pr-2 font-mono scroll-bg scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {meetingTranscript.length === 0 ? (
            <p className="text-xs text-[var(--text-dim)] italic text-center py-20">
              {isEn ? "Meeting history will appear here when you start..." : "El historial de la reunión aparecerá aquí al comenzar..."}
            </p>
          ) : (
            meetingTranscript.map((item, index) => (
              <div
                key={index}
                className={`text-xs leading-relaxed p-1.5 rounded transition-all ${
                  item.isMic ? "bg-emerald-500/5 text-emerald-400" : "text-[var(--text-muted)]"
                }`}
              >
                <span className="text-[var(--text-dim)] mr-2.5 font-sans select-none">[{item.timestamp}]</span>
                <span className="select-text">{item.orig}</span>
              </div>
            ))
          )}
        </div>

        {meetingTranscript.length > 0 && !recording && (
          <div className="mt-5 pt-4 border-t border-[var(--border)] flex justify-center">
              <button
                onClick={onOpenSummary}
                className="flex items-center gap-2 px-5 py-2.5 bg-[var(--accent)] hover:bg-[#006ee6] text-white font-semibold text-xs rounded-lg shadow-lg active:scale-95 transition-all"
              >
                <span>📝</span>
                {isEn ? "Generate Minutes & Agreements with AI" : "Generar Minuta & Acuerdos con IA"}
              </button>
          </div>
        )}
      </div>
    </div>
  );
};
